/**
 * Notifications (toasts).
 *
 *   g.notify(message, opts)   generic (opts.type decides the look)
 *   g.success(message, opts)  g.error(...)  g.warning(...)  g.info(...)
 *
 * Auto-hide is PER TYPE (notify.defaults.durations): errors are sticky by
 * default (duration 0 - stay until closed), the other types hide after 5s;
 * opts.duration overrides per call. With opts.shake the toast also shakes a
 * target element (g.shake, glow type follows the toast type) - the toast is
 * then "linked": clicking it re-scrolls/re-shakes the target. An identical
 * toast (type+title+message+position) still on screen is never duplicated:
 * the existing one bumps (re-flash + countdown restart + re-shake) instead.
 *
 * Defaults live in notify.defaults and every call can override them via opts.
 * With { nextPage: true } the notification is not shown now: it is queued in
 * sessionStorage and displayed automatically on the next page load (the queue
 * is flushed below, on DOMContentLoaded; an element shake target cannot ride
 * along - pass a selector). The library itself never reloads or redirects -
 * that is the caller's job.
 *
 * Markup is generated here; styles in src/scss/_notify.scss (.genus-toast*).
 * Full option reference: docs/libs/genus.md.
 */
import { translate } from './translate.js';
import { shake } from './shake.js';

const STORAGE_KEY = 'genus-notify-next';

const TYPES = {
	success: { icon: 'ri-checkbox-circle-fill' },
	error: { icon: 'ri-close-circle-fill' },
	warning: { icon: 'ri-alert-fill' },
	info: { icon: 'ri-information-fill' },
};

const POSITIONS = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'];

// dedup key -> the live toast element (cleared when the toast leaves)
const live = new Map();

export function notify(message, opts) {
	opts = { ...notify.defaults, ...opts };
	if (opts.nextPage) {
		enqueue(message, opts);
		return null;
	}
	return show(message, opts);
}

// Global defaults. Override per call via opts, or for the whole app by
// mutating this object (e.g. g.notify.defaults.position = 'top-center').
// duration null = the TYPE's default from durations; a number (per call or
// set here) forces every type.
notify.defaults = {
	type: 'info',        // success | error | warning | info
	position: 'bottom-right', // top|bottom - left|center|right
	duration: null,      // ms until auto-hide; 0 = stays until closed; null = durations[type]
	durations: { success: 5000, error: 0, warning: 5000, info: 5000 },
	dismissible: true,   // show the close (x) button
	title: '',           // optional bold first line
	icon: '',            // ri-* class override; '' = the type's default icon
	html: false,         // render message as HTML (only for trusted strings)
	shake: null,         // element | selector | { el, ...g.shake opts } shaken with the toast
	nextPage: false,     // queue for the next page load instead of showing now
};

export const success = (message, opts) => notify(message, { ...opts, type: 'success' });
export const error = (message, opts) => notify(message, { ...opts, type: 'error' });
export const warning = (message, opts) => notify(message, { ...opts, type: 'warning' });
export const info = (message, opts) => notify(message, { ...opts, type: 'info' });

// One fixed container per position, created on demand, removed when empty.
function getContainer(position) {
	if (!POSITIONS.includes(position)) position = notify.defaults.position;
	let el = document.querySelector(`.genus-toasts[data-position="${position}"]`);
	if (!el) {
		el = document.createElement('div');
		el.className = `genus-toasts genus-toasts-${position}`;
		el.setAttribute('data-position', position);
		document.body.appendChild(el);
	}
	return el;
}

// shake option -> { el, ...opts } or null (invalid/absent)
function shakeSpec(value) {
	if (!value) return null;
	if (typeof value === 'string' || value instanceof HTMLElement) return { el: value };
	return value.el ? value : null;
}

function runShake(spec, type) {
	// the glow follows the toast type; an explicit spec.type still wins
	shake(spec.el, { type, ...spec });
}

// (Re)arm the auto-hide countdown; duration rides the element for the
// hover-pause listeners and the dedup bump.
function arm(toast) {
	clearTimeout(toast._genusTimer);
	if (toast._genusDuration > 0) toast._genusTimer = setTimeout(() => hide(toast), toast._genusDuration);
}

// An identical toast is already on screen: re-flash it, restart its
// countdown and re-run its shake instead of stacking a twin.
function bump(toast) {
	toast.classList.remove('genus-toast-bump');
	void toast.offsetWidth; // reflow so re-adding restarts the animation
	toast.classList.add('genus-toast-bump');
	setTimeout(() => toast.classList.remove('genus-toast-bump'), 400);
	arm(toast);
	if (toast._genusShake) runShake(toast._genusShake, toast._genusType);
	return toast;
}

function show(message, opts) {
	const type = TYPES[opts.type] ? opts.type : 'info';
	const key = [type, opts.position, opts.title, message].join('\n');

	const existing = live.get(key);
	if (existing && existing.isConnected) return bump(existing);

	const toast = document.createElement('div');
	toast.className = `genus-toast genus-toast-${type}`;
	toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
	toast._genusType = type;
	toast._genusKey = key;
	toast._genusDuration = opts.duration ?? (opts.durations || notify.defaults.durations)[type] ?? 0;
	live.set(key, toast);

	const icon = document.createElement('i');
	icon.className = `genus-toast-icon ${opts.icon || TYPES[type].icon}`;
	icon.setAttribute('aria-hidden', 'true');
	toast.appendChild(icon);

	const content = document.createElement('div');
	content.className = 'genus-toast-content';
	if (opts.title) {
		const title = document.createElement('div');
		title.className = 'genus-toast-title';
		title.textContent = opts.title;
		content.appendChild(title);
	}
	const msg = document.createElement('div');
	msg.className = 'genus-toast-message';
	if (opts.html) msg.innerHTML = message;
	else msg.textContent = message;
	content.appendChild(msg);
	toast.appendChild(content);

	if (opts.dismissible) {
		const close = document.createElement('button');
		close.type = 'button';
		close.className = 'genus-toast-close';
		close.setAttribute('aria-label', translate('Close notification'));
		close.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
		close.addEventListener('click', () => hide(toast));
		toast.appendChild(close);
	}

	// linked toast: shake now, and clicking the toast re-points at the target
	const spec = shakeSpec(opts.shake);
	if (spec) {
		toast._genusShake = spec;
		toast.classList.add('genus-toast-linked');
		toast.addEventListener('click', (e) => {
			if (e.target.closest('.genus-toast-close')) return;
			runShake(spec, type);
		});
		runShake(spec, type);
	}

	getContainer(opts.position).appendChild(toast);

	// Two frames so the browser paints the initial state first and the entry
	// transition actually runs.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => toast.classList.add('show'));
	});

	// Auto-hide; hovering pauses the countdown (restarts full on leave).
	arm(toast);
	toast.addEventListener('mouseenter', () => clearTimeout(toast._genusTimer));
	toast.addEventListener('mouseleave', () => arm(toast));

	return toast;
}

function hide(toast) {
	if (!toast.parentNode) return;
	clearTimeout(toast._genusTimer);
	if (live.get(toast._genusKey) === toast) live.delete(toast._genusKey);
	toast.classList.remove('show');
	let removed = false;
	const remove = () => {
		if (removed) return;
		removed = true;
		const parent = toast.parentNode;
		if (parent) {
			parent.removeChild(toast);
			if (!parent.children.length) parent.remove();
		}
	};
	toast.addEventListener('transitionend', remove);
	setTimeout(remove, 300); // fallback (prefers-reduced-motion has no transition)
}

// ---- nextPage queue (sessionStorage) ----------------------------------------
function readQueue() {
	try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
}

function enqueue(message, opts) {
	const list = readQueue();
	const item = { ...opts, nextPage: false };
	// an HTMLElement cannot ride sessionStorage - only selector targets survive
	const spec = shakeSpec(item.shake);
	item.shake = spec && typeof spec.el === 'string' ? spec : null;
	list.push({ message: String(message), opts: item });
	try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

function flushQueue() {
	const list = readQueue();
	try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
	list.forEach((item) => notify(item.message, item.opts));
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', flushQueue);
} else {
	flushQueue();
}
